import React, { useState, useMemo } from 'react';
import { FileText, ListTodo, Images, Microscope, ClipboardList, ChevronDown, Settings, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useUserProfile } from '@/hooks/profile/useUserProfile';
import { useProjectNavigation } from '@/hooks/goal-planner/use-optimized-navigation';
import { useUpdateProjectSettings, getTabSettings, TabSettings } from '@/hooks/project/use-project-settings';
import { useToast } from '@/hooks/errors/use-toast';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface ProjectSettingsContainerProps {
  pageId: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const ProjectSettingsContainer: React.FC<ProjectSettingsContainerProps> = ({ 
  pageId,
  isOpen,
  onToggle 
}) => {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { profile } = useUserProfile();
  const { data: navigationData } = useProjectNavigation(pageId);
  const updateProjectSettings = useUpdateProjectSettings();
  
  // Get tab settings from project data
  const tabSettings = useMemo(() => {
    return getTabSettings(navigationData?.project?.settings);
  }, [navigationData]);

  const [formData, setFormData] = useState<TabSettings>(tabSettings);

  // Sync with parent state if provided
  React.useEffect(() => {
    if (isOpen !== undefined) {
      setIsExpanded(isOpen);
    }
  }, [isOpen]);
  
  // Update form data when settings load
  React.useEffect(() => {
    setFormData(tabSettings);
  }, [tabSettings]);

  const handleToggle = (tab: keyof TabSettings) => {
    setHasChanges(true);
    setFormData(prev => ({
      ...prev,
      [tab]: !prev[tab]
    }));
  };

  const handleSave = async () => {
    try {
      await updateProjectSettings.mutateAsync({
        projectId: pageId,
        settings: {
          navigation: {
            tabs: formData
          }
        }
      });
      
      toast({
        title: 'Navigation Settings Updated',
        description: 'Your project navigation tabs have been updated successfully',
        variant: 'default',
      });
      
      setHasChanges(false);
      setIsExpanded(false); // Close accordion after save
    } catch (error) {
      console.error('Failed to update project settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to update navigation settings',
        variant: 'destructive',
      });
    }
  };

  // Count enabled tabs
  const enabledTabsCount = Object.values(formData).filter(Boolean).length;
  const isComplete = enabledTabsCount >= 2; // At least 2 tabs enabled

  const tabs = [
    {
      key: 'tasks' as keyof TabSettings,
      label: 'Tasks',
      icon: ListTodo,
      description: 'View and manage project tasks',
      color: 'amber'
    },
    {
      key: 'documents' as keyof TabSettings,
      label: 'Documents',
      icon: FileText,
      description: 'Create and edit project documents',
      color: 'purple'
    },
    {
      key: 'research' as keyof TabSettings,
      label: 'Research',
      icon: Microscope,
      description: 'Access research materials',
      color: 'green'
    },
    {
      key: 'images' as keyof TabSettings,
      label: 'Images',
      icon: Images,
      description: 'Manage project images',
      color: 'blue'
    },
    ...(profile?.finalpieceStaff ? [{
      key: 'work_order' as keyof TabSettings,
      label: 'Work Orders',
      icon: ClipboardList,
      description: 'Create and manage work orders',
      color: 'indigo'
    }] : [])
  ];

  const getEnabledTabsText = () => {
    const enabledTabs = tabs.filter(tab => formData[tab.key]).map(tab => tab.label);
    if (enabledTabs.length === 0) return 'No tabs enabled';
    if (enabledTabs.length === 1) return enabledTabs[0];
    if (enabledTabs.length === 2) return `${enabledTabs[0]} and ${enabledTabs[1]}`;
    return `${enabledTabs.slice(0, -1).join(', ')}, and ${enabledTabs[enabledTabs.length - 1]}`;
  };

  return (
    <div className="space-y-4">
      {/* Accordion Header */}
      <button
        onClick={() => {
          const newState = !isExpanded;
          if (onToggle) {
            onToggle(newState);
          } else {
            setIsExpanded(newState);
          }
        }}
        className={cn(
          "w-full flex items-center justify-between p-4 rounded-lg transition-all",
          "bg-white dark:bg-gray-800",
          "border border-gray-200 dark:border-gray-700",
          "hover:border-purple-300 dark:hover:border-purple-600",
          isExpanded && "border-purple-300 dark:border-purple-600 shadow-sm"
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2 rounded-lg transition-colors",
            isComplete 
              ? "bg-purple-100 dark:bg-purple-900/30" 
              : "bg-gray-100 dark:bg-gray-900/30"
          )}>
            <Settings className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Project Settings
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Customize your project navigation tabs and features
            </p>
          </div>
        </div>
        <ChevronDown 
          className={cn(
            "h-5 w-5 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )} 
        />
      </button>

      {/* Accordion Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-6">
              {/* Edit Form (Always shown when expanded) */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-base font-medium">Navigation Tabs</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Choose which tabs appear in your project navigation bar
                  </p>
                </div>
                
                <div className="space-y-3">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isEnabled = formData[tab.key];
                    
                    return (
                      <div
                        key={tab.key}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border transition-all",
                          isEnabled
                            ? "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                            : "bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800 opacity-60"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            isEnabled ? (
                              tab.color === 'amber' ? "bg-amber-100 dark:bg-amber-900/30" :
                              tab.color === 'purple' ? "bg-purple-100 dark:bg-purple-900/30" :
                              tab.color === 'green' ? "bg-green-100 dark:bg-green-900/30" :
                              tab.color === 'blue' ? "bg-blue-100 dark:bg-blue-900/30" :
                              tab.color === 'indigo' ? "bg-indigo-100 dark:bg-indigo-900/30" :
                              "bg-gray-100 dark:bg-gray-800"
                            ) : "bg-gray-100 dark:bg-gray-800"
                          )}>
                            <Icon 
                              size={16} 
                              className={cn(
                                isEnabled ? (
                                  tab.color === 'amber' ? "text-amber-600 dark:text-amber-400" :
                                  tab.color === 'purple' ? "text-purple-600 dark:text-purple-400" :
                                  tab.color === 'green' ? "text-green-600 dark:text-green-400" :
                                  tab.color === 'blue' ? "text-blue-600 dark:text-blue-400" :
                                  tab.color === 'indigo' ? "text-indigo-600 dark:text-indigo-400" :
                                  "text-gray-600 dark:text-gray-400"
                                ) : "text-gray-400 dark:text-gray-500"
                              )} 
                            />
                          </div>
                          <div>
                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                              {tab.label}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {tab.description}
                            </div>
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => handleToggle(tab.key)}
                          className="data-[state=checked]:bg-purple-600"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsExpanded(false);
                    setFormData(tabSettings);
                    setHasChanges(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateProjectSettings.isPending || !hasChanges}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  {updateProjectSettings.isPending ? (
                    <>
                      <Save className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectSettingsContainer;